import { parseFormation } from "./formation-parser";
import { parsePositionCode, type Side, type Line, type PositionCode } from "./position-parser";

export interface Player {
  id: string;
  playerId: string;
  playerImpectId: string;
  name: string;
  shirtNo?: number | null;
  positionRaw: string;
  side_id: string;
}

export interface PlacedPlayer extends Player {
  row: number;
  col: number;
}

// Internal working type (extends Player with parsed fields)
type Cand = Player & { pref: number; prio: number; side: Side; role: string; line: Line };

export function placePlayers(
  players: Player[],
  formation: string,
  isAway = false
): PlacedPlayer[] {
  const rowCounts = parseFormation(formation);   // e.g., [4,2,3,1]
  const L = rowCounts.length;
  if (!L) return [];

  // Build candidate list (adds side/line/role, pref row, priority)
  const candidates: Cand[] = players.map((p) => {
    const pos = safeParsePosition(p.positionRaw);
    return {
      ...p,
      pref: preferredRowIndex(pos, L),
      prio: rolePriority(pos),
      side: pos.side,
      role: pos.role,
      line: pos.line,
    };
  });

  // Place “shape-defining” roles first
  candidates.sort((a, b) => a.prio - b.prio);

  // Per-row buckets + remaining capacity (NOTE: Cand[][])
  const buckets: Cand[][] = Array.from({ length: L }, () => []);
  const remaining = rowCounts.slice();

  for (const c of candidates) {
    const r = findNearestRowWithCapacity(remaining, c.pref);
    if (r !== -1) {
      buckets[r].push(c);
      remaining[r] -= 1;
    } else {
      // eslint-disable-next-line no-console
      console.warn(`[formation-placement] No capacity left to place "${c.name}" (${c.positionRaw}).`);
    }
  }

  // Columns per row
  const placed: PlacedPlayer[] = [];
  for (let rowIdx = 0; rowIdx < L; rowIdx++) {
    const group = (buckets[rowIdx] ?? []).slice();
    const rowSize = rowCounts[rowIdx];

    // Stable order before column slotting
    group.sort(
        (a, b) => sideOrder(flipSideForAway(a.side, isAway)) - sideOrder(flipSideForAway(b.side, isAway))
    );

    const cols = assignColumns(rowSize, group, (p) => flipSideForAway(p.side, isAway));
    const displayRow = isAway ? (L - 1 - rowIdx) : rowIdx;

    cols.forEach((pl, col) => {
      if (pl) placed.push({ ...pl, row: displayRow, col });
    });
  }

  return placed;
}

/* ---------------- helpers ---------------- */

function flipSideForAway(side: Side, isAway: boolean): Side {
  if (!isAway) return side;
  return side === "LEFT" ? "RIGHT" : side === "RIGHT" ? "LEFT" : "CENTER";
}

function safeParsePosition(positionRaw: string) {
  try {
    return parsePositionCode(positionRaw);
  } catch {
    return { raw: positionRaw, side: "CENTER" as Side, line: "MIDFIELD" as Line, role: "UNKNOWN" };
  }
}

/** Preferred row by role/line; tuned for common roles. */
function preferredRowIndex(pos: PositionCode, L: number): number {
  const back = 0;
  const front = Math.max(0, L - 1);
  const mid = Math.floor(L / 2);
  const midLow = Math.max(1, mid - 1);
  const midHigh = Math.min(Math.max(0, L - 1), mid + 1);

  const r = pos.role;

  if (r.includes("CENTERBACK")) return back;
  if (r.includes("FULLBACK") || r.includes("WINGBACK")) return back;

  if (r.includes("DEFENSIVE_MIDFIELDER")) return L >= 4 ? midLow : mid;
  if (r.includes("CENTRAL_MIDFIELDER") || r.includes("BOX_TO_BOX")) return mid;
  if (r.includes("ATTACKING_MIDFIELDER")) return L >= 4 ? midHigh : (L >= 3 ? mid : front);

  if (r.includes("WINGER")) return L >= 3 ? front : mid;
  if (r.includes("SECOND_STRIKER")) return front;
  if (r.includes("CENTER_FORWARD")) return front;

  switch (pos.line) {
    case "DEFENDER": return back;
    case "MIDFIELD": return mid;
    case "ATTACKING_MIDFIELD": return L >= 4 ? midHigh : (L >= 3 ? mid : front);
    case "FORWARD": return front;
    case "GOALKEEPER": return back;
    default: return mid;
  }
}

/** Lower number = earlier placement (more structural). */
function rolePriority(pos: PositionCode): number {
  const r = pos.role;
  if (r.includes("CENTERBACK")) return 0;
  if (r.includes("CENTER_FORWARD")) return 1;
  if (r.includes("WINGER")) return 2;
  if (r.includes("ATTACKING_MIDFIELDER")) return 3;
  if (r.includes("DEFENSIVE_MIDFIELDER")) return 4;
  if (r.includes("FULLBACK") || r.includes("WINGBACK")) return 5;
  if (r.includes("CENTRAL_MIDFIELDER") || r.includes("BOX_TO_BOX")) return 6;
  switch (pos.line) {
    case "DEFENDER": return 7;
    case "FORWARD": return 8;
    case "ATTACKING_MIDFIELD": return 9;
    case "MIDFIELD": return 10;
    default: return 11;
  }
}

function findNearestRowWithCapacity(remaining: number[], target: number): number {
  if (remaining[target] > 0) return target;
  for (let r = 1; r < remaining.length; r++) {
    const left = target - r;
    const right = target + r;
    if (left >= 0 && remaining[left] > 0) return left;
    if (right < remaining.length && remaining[right] > 0) return right;
  }
  return -1;
}

function sideOrder(side: Side): number {
  return side === "CENTER" ? 0 : side === "LEFT" ? 1 : 2;
}

/** Columns: CENTER middle-out, LEFT from left edge, RIGHT from right edge. */
function assignColumns<T extends { id: string }>(
  rowSize: number,
  group: T[],
  getSide: (p: T) => Side
): Array<T | null> {
  const cols = new Array<T | null>(rowSize).fill(null);
  const centers: T[] = [], lefts: T[] = [], rights: T[] = [];
  for (const p of group) {
    const s = getSide(p);
    if (s === "CENTER") centers.push(p);
    else if (s === "LEFT") lefts.push(p);
    else rights.push(p);
  }

  const mid = Math.floor((rowSize - 1) / 2);
  let offset = 0;
  for (const p of centers) {
    const delta = Math.ceil(offset / 2) * (offset % 2 === 0 ? 1 : -1);
    const c = clamp(mid + delta, 0, rowSize - 1);
    const idx = nearestFree(cols, c);
    if (idx !== -1) cols[idx] = p;
    offset++;
  }
  for (const p of lefts) {
    const idx = firstFree(cols, +1);
    if (idx !== -1) cols[idx] = p;
  }
  for (const p of rights) {
    const idx = firstFree(cols, -1);
    if (idx !== -1) cols[idx] = p;
  }
  return cols;
}

function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }
function nearestFree<T>(cols: Array<T | null>, target: number) {
  if (cols[target] === null) return target;
  for (let r = 1; r < cols.length; r++) {
    const L = target - r, R = target + r;
    if (L >= 0 && cols[L] === null) return L;
    if (R < cols.length && cols[R] === null) return R;
  }
  return -1;
}
function firstFree<T>(cols: Array<T | null>, dir: 1 | -1) {
  if (dir === 1) { for (let i = 0; i < cols.length; i++) if (cols[i] === null) return i; }
  else { for (let i = cols.length - 1; i >= 0; i--) if (cols[i] === null) return i; }
  return -1;
}

/** Convenience for the view layer */
export function getPlayerAt(placed: PlacedPlayer[], row: number, col: number) {
  return placed.find((p) => p.row === row && p.col === col);
}
